import React, { createContext, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useLanguage } from "./LanguageProvider";

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const SESSION_DURATION = 60 * 60 * 1000; // 1 hour in ms
  const STORAGE_KEY = "admin_session_expiry";

  const [isAdmin, setIsAdmin] = useState(() => {
    const expiry = localStorage.getItem(STORAGE_KEY);
    if (expiry && parseInt(expiry, 10) > Date.now()) {
      return true;
    }
    return false;
  });

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const ADMIN_PASSWORD = "badmintonadmin2025";
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handlePasswordSubmit = async () => {
    if (isLoggingIn) return;
    setIsLoggingIn(true);
    
    // De-bounce and simulate a smooth "verification" experience
    setTimeout(() => {
      if (passwordInput === ADMIN_PASSWORD) {
        setIsAdmin(true);
        localStorage.setItem(STORAGE_KEY, (Date.now() + SESSION_DURATION).toString());
        setPasswordInput("");
        setShowPasswordModal(false);
        setIsLoggingIn(false);
        toast.success(t("login_success"));
        navigate("/");
      } else {
        toast.error(t("password_wrong"));
        setPasswordInput("");
        setIsLoggingIn(false);
      }
    }, 600);
  };

  const logout = () => {
    setIsAdmin(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <AdminContext.Provider value={{ isAdmin, setShowPasswordModal, logout }}>
      {/* Password Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <div className="modal-overlay-wrapper centered">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="modal-backdrop"
              onClick={() => setShowPasswordModal(false)}
            />
            <div className="modal-container">
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                className="confirm-modal info"
                onClick={(e) => e.stopPropagation()}
              >
                <button 
                  className="modal-close-btn" 
                  onClick={() => setShowPasswordModal(false)}
                >
                  <X size={20} />
                </button>

                <div className="modal-content-area">
                  <h2 className="modal-title">{t("enter_admin_password")}</h2>
                </div>

                <div className="modal-body" style={{ width: '100%' }}>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => setPasswordInput(e.target.value)}
                    placeholder={t("password_placeholder")}
                    className="input"
                    disabled={isLoggingIn}
                    onKeyDown={(e) => e.key === "Enter" && handlePasswordSubmit()}
                  />
                  <div className="modal-actions-horizontal" style={{ marginTop: '1.5rem' }}>
                    <button
                      className="modal-btn-secondary"
                      onClick={() => setShowPasswordModal(false)}
                    >
                      {t("cancel")}
                    </button>
                    <button
                      onClick={handlePasswordSubmit}
                      className="modal-btn-primary info"
                    >
                      {t("confirm")}
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {children}
    </AdminContext.Provider>
  );
};
